import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Icon,
} from "@fluentui/react";
import { formatISO } from "date-fns/esm";
import { Formik } from "formik";
import { useSetVehicleLicensePlate } from "hooks/data/mutations/useSetVehicleLicensePlate";
import usePhraseActivationStatus from "hooks/usePhraseActivationStatus";
import moment from "moment";
import { isNoLicensePlate } from "pages/vehicle/helpers";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useSelector } from "store/hooks";
import { setVehicle } from "store/vehicle";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { toast } from "./FluentToast";
import { FormSpinner } from "./FormSpinner";
import { Field } from "./form/Field";
import { combine, maxLength, required } from "./form/validation";
import { StyledPrimaryButton } from "./styled";

type Props = {
  close: () => void;
};

const toISODate = (date: Date) => formatISO(date, { representation: "date" });

// external users replace the "no license plate" value (KEINES/NONE) of an activated vehicle with the real license plate;
// on the validFrom date of the KEINES/NONE period its value is corrected, on a later date the license plate is changed from that date
export const SetLicensePlateDialog = ({ close }: Props) => {
  const theme = useTheme() as IStyledTheme;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [didTrySubmit, setDidTrySubmit] = useState(false);
  const phraseActivated = usePhraseActivationStatus().activated;
  const rowVersion = useSelector((s) => s.vehicle.vehicle?.rowVersion);
  const licensePlateHistory = useSelector(
    (s) => s.vehicle.vehicle?.vehicleLicensePlateHistoryData
  );
  const { fleetId, vehicleId } = useParams() as any;

  // validFrom of the current (last) license plate period, i.e. the KEINES/NONE one - default and minimum date
  const noLicensePlateValidFrom = useMemo(() => {
    const validFromDates = (licensePlateHistory ?? []).map((lp) =>
      moment(lp.validFromDate).startOf("day").toDate()
    );
    return validFromDates.length
      ? new Date(Math.max(...validFromDates.map((d) => d.getTime())))
      : moment().startOf("day").toDate();
  }, [licensePlateHistory]);

  const { mutate: onSetVehicleLicensePlate, isLoading } =
    useSetVehicleLicensePlate();

  const onSubmit = (data) => {
    onSetVehicleLicensePlate(
      {
        rowVersion,
        params: {
          fleetId,
          vehicleId,
          body: {
            licensePlate: data.licensePlate.trim(),
            validFromDate: toISODate(data.validFromDate),
            comment: data.comment,
          },
        },
      },
      {
        onSuccess: (res) => {
          dispatch(setVehicle(res.data));
          toast.success(t("bfm.success"));
          close();
        },
      }
    );
  };

  const title = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Icon
        iconName="Edit"
        style={{
          color: theme.palette.themePrimary,
          fontSize: "20px",
          marginRight: "5px",
        }}
      />
      {t("bfm.setLicensePlate.label")}
    </div>
  );

  return (
    <Dialog
      hidden={false}
      onDismiss={close}
      dialogContentProps={{
        type: DialogType.normal,
        title,
        showCloseButton: true,
      }}
      minWidth={400}
      modalProps={{
        isBlocking: phraseActivated ? false : true,
      }}
    >
      <Formik
        initialValues={{
          licensePlate: "",
          validFromDate: noLicensePlateValidFrom,
          comment: "",
        }}
        onSubmit={onSubmit}
        validateOnChange={didTrySubmit}
        validateOnBlur={didTrySubmit}
      >
        {({ submitForm }) => {
          return (
            <>
              <Field
                name="licensePlate"
                type="text"
                label={"bfm.vehicleForm.licensePlate.label"}
                isRequired
                validate={(value) =>
                  combine(
                    required(value?.trim()),
                    maxLength(value?.trim(), 12, t),
                    isNoLicensePlate(value)
                      ? t("bfm.setLicensePlate.noLicensePlateNotAllowed")
                      : undefined
                  )
                }
              />
              <Field
                name="validFromDate"
                type="date"
                label={"bfm.vehicleForm.validFromDate.label"}
                isRequired
                minDate={noLicensePlateValidFrom}
                validate={(value) =>
                  combine(
                    required(value),
                    value &&
                      toISODate(value) < toISODate(noLicensePlateValidFrom)
                      ? t("bfm.setLicensePlate.validFromDateTooEarly", {
                          date: moment(noLicensePlateValidFrom).format(
                            "DD.MM.YYYY"
                          ),
                        })
                      : undefined
                  )
                }
                d={({ values, errors, setFieldValue }) => ({
                  values,
                  errors,
                  setFieldValue,
                  defaultValue: values?.validFromDate,
                })}
              />
              <Field
                name="comment"
                type="text"
                label={"bfm.comment.label"}
                isMultiline
                rows={4}
                isRequired={false}
                validate={(value) => maxLength(value, 512, t)}
              />
              <DialogFooter>
                <StyledPrimaryButton
                  onClick={() => {
                    setDidTrySubmit(true);
                    submitForm();
                  }}
                  text={t("greco.save")}
                  disabled={isLoading}
                  style={{
                    marginRight: "5px",
                  }}
                />
                <DefaultButton onClick={close} text={t("bfm.close.label")} />
              </DialogFooter>
              {isLoading && <FormSpinner />}
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};
